import { create } from 'zustand';
import { toZonedTime } from 'date-fns-tz';
import { initialTime, localDateTime } from '@/services/initialTime';
import {
  calculateEndDateTime,
  durationToString,
  formatDate,
  formatTime,
  parseDateTime,
} from '@/services/dateTimeUtility';
import { StripeDetailsProps } from '@/components/Utilities/StripeConnection';
import { getBrowserTimeZone } from '@/components/Utilities/FormatDateTime';
import { StudioDetails } from '@/components/CreateStudio/types';
import { flattenAttributes } from '@/components/Utilities/helper';
import { UserReservation } from '@/hooks/useUserReservations';

export interface StudioQuery {
  query?: string | undefined | null;
  date: Date | null;
  time: string | undefined | null;
  duration: number | null;
  endTime?: string | undefined | null;
  endDate?: string | undefined | null;
}

export interface StudioSearchQuery {
  studioName?: string;
  roomName?: string;
  address?: string;
}

// initialTime() returns the current time rounded to the nearest 30-minute interval
const getURLParams = () => {
  const urlParam = new URLSearchParams(window.location.search);
  return {
    query: urlParam.get('query') || '',
    date: urlParam.get('date') || null,
    time: urlParam.get('time') || initialTime(),
    duration: parseInt(urlParam.get('duration') || '120', 10),
  };
};

const urlParams = getURLParams();
const zonedDate = urlParams.date
  ? toZonedTime(new Date(urlParams.date), getBrowserTimeZone())
  : localDateTime;

const initialStudioQuery: StudioQuery = {
  query: urlParams.query,
  date: urlParams.date ? zonedDate : localDateTime,
  time: urlParams.time,
  duration: urlParams.duration,
};

const getStudioSearchQuery = (): StudioSearchQuery => {
  const { studioName, roomName, address } = JSON.parse(
    sessionStorage.getItem('studioSearchQuery') || '{}',
  );
  return { studioName, roomName, address };
};

interface StudioForm {
  images: (string | File)[];
}

interface StudioFormStore {
  // Fill in all the setters
  formData: StudioForm;
  setFormData: (formData: StudioForm) => void;
  setImages: (images: (string | File)[]) => void;
  isDeleteModalVisible: boolean;
  updateDeleteModalVisibility: () => void;
  studioDetails: StudioDetails | null;
  setStudioDetails: (studioDetails: StudioDetails | null) => void;
  submitBtnLoading: boolean;
  updateSubmitBtnLoading: (loading: boolean) => void;
}

interface StudioQueryStore {
  studioQuery: StudioQuery;
  studioSearchQuery: StudioSearchQuery;
  setStudioSearchQuery: (studioSearchQuery: StudioSearchQuery) => void;
  setQuery: (query: string) => void;
  setDate: (date: Date) => void;
  setTime: (time: string) => void;
  setDuration: (duration: number) => void;
  resetStudioQuery: () => void;
  calculateEnd: () => void;
  getFormattedDateTime: () => {
    formattedDate: string;
    formattedTime: string;
    formattedEndDate: string;
    formattedEndTime: string;
    durationInString: string;
  } | null;
}

export const useStudioQueryStore = create<StudioQueryStore>((set, get) => ({
  studioQuery: initialStudioQuery,
  studioSearchQuery: getStudioSearchQuery(),
  setStudioSearchQuery: (query: StudioSearchQuery) => {
    sessionStorage.setItem('studioSearchQuery', JSON.stringify(query));
    set(() => ({ studioSearchQuery: query }));
  },
  setQuery: (query) =>
    set((state) => ({ studioQuery: { ...state.studioQuery, query } })),
  setDate: (date) =>
    set((state) => ({ studioQuery: { ...state.studioQuery, date } })),
  setTime: (time) =>
    set((state) => ({ studioQuery: { ...state.studioQuery, time } })),
  setDuration: (duration) => {
    if (duration >= 0) {
      set((state) => ({ studioQuery: { ...state.studioQuery, duration } }));
    }
  },
  // Calculating End Time Date based on date time duration
  calculateEnd: () => {
    const { date, time, duration } = get().studioQuery;
    if (date && time && duration != null) {
      const startDateTime = parseDateTime(date, time);
      const endDateTime = calculateEndDateTime(startDateTime, duration);

      set((state) => ({
        studioQuery: {
          ...state.studioQuery,
          endTime: formatTime(endDateTime),
          endDate: formatDate(endDateTime),
        },
      }));
    }
  },
  resetStudioQuery: () => {
    set({ studioQuery: initialStudioQuery });
  },
  getFormattedDateTime: () => {
    const { date, time, duration } = get().studioQuery;
    if (date && time && duration != null) {
      const startDateTime = parseDateTime(date, time);
      const endDateTime = calculateEndDateTime(startDateTime, duration);
      return {
        formattedDate: formatDate(startDateTime),
        formattedTime: formatTime(startDateTime),
        formattedEndDate: formatDate(endDateTime),
        formattedEndTime: formatTime(endDateTime),
        durationInString: durationToString(duration),
      };
    }
    return null;
  },
}));

const initialFormState: StudioForm = {
  images: [],
};

export const useStudioStore = create<StudioFormStore>((set) => ({
  formData: initialFormState,
  setFormData: (formData) => set({ formData }),
  setImages: (images) =>
    set((state) => ({ formData: { ...state.formData, images } })),
  isDeleteModalVisible: false,
  updateDeleteModalVisibility: () =>
    set((state) => ({ isDeleteModalVisible: !state.isDeleteModalVisible })),
  studioDetails: null,
  setStudioDetails: (studioDetails) =>
    set({
      studioDetails,
      formData: { images: studioDetails?.imagesUpload || [] },
    }),
  submitBtnLoading: false,
  updateSubmitBtnLoading: (loading: boolean) =>
    set({ submitBtnLoading: loading }),
}));

interface User {
  firstName?: string;
  lastName?: string;
  mobileNumber?: string;
  dateOfBirth?: string;
  gender?: string;
  image?: string;
}

interface UserProfileStore {
  userData: User;
  setUserData: (userData: Partial<User>) => void;
  setDateOfBirth: (dateOfBirth: string) => void;
  canEditContact: boolean;
  isContactModalVisible: boolean;
  isOTPModalVisible: boolean;
  isBtnLoading: boolean;
  isResendBtnLoading: boolean;
  setCanEditContact: (canEditContact: boolean) => void;
  setContactModalVisibility: (contactModalVisible: boolean) => void;
  setOTPModalVisibility: (OTPModalVisible: boolean) => void;
  setIsBtnLoading: (loading: boolean) => void;
  setResendIsBtnLoading: (loading: boolean) => void;
}

export const useUserProfileStore = create<UserProfileStore>((set) => ({
  userData: {
    firstName: '',
    lastName: '',
    mobileNumber: '',
    dateOfBirth: '',
    gender: '',
    image: '',
  },
  canEditContact: true,
  isContactModalVisible: false,
  isOTPModalVisible: false,
  isBtnLoading: false,
  isResendBtnLoading: false,
  setUserData: (userData) =>
    set((state) => ({ userData: { ...state.userData, ...userData } })),
  setDateOfBirth: (dateOfBirth) =>
    set((state) => ({ userData: { ...state.userData, dateOfBirth } })),
  setContactModalVisibility: (visible: boolean) =>
    set(() => ({ isContactModalVisible: visible })),
  setOTPModalVisibility: (visible: boolean) =>
    set(() => ({ isOTPModalVisible: visible })),
  setCanEditContact: (canEdit: boolean) =>
    set(() => ({ canEditContact: canEdit })),
  setIsBtnLoading: (loading: boolean) => set(() => ({ isBtnLoading: loading })),
  setResendIsBtnLoading: (loading: boolean) =>
    set(() => ({ isResendBtnLoading: loading })),
}));

// -- Start of User Authentication -- //

export interface UserDetails {
  allowPasswordChange: boolean;
  approvedByAdmin: boolean;
  dateOfBirth: string | null;
  email: string;
  admin: boolean;
  authenticationToken: string;
  firstName: string;
  gender: string;
  id: number | undefined;
  image: string | null;
  lastName: string;
  mobileNumber: string | null;
  provider: string;
  studioOwner?: boolean;
  uid: string;
  stripeAccountId?: string;
  stripeAccountStatus?: string | null;
  isAdmin?: boolean;
  otpFailedAt?: string;
  otpSentAt?: string;
  otpVerifiedAt?: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  user: UserDetails | null;
  setAuth: (user: UserDetails) => void;
  clearAuth: () => void;
  isLoading: boolean;
  setLoading: (isLoading: boolean) => void;
  studioOwner?: boolean;
}

export const useAuthStore = create<AuthState>((set) => ({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  setAuth: (user) => set({ isAuthenticated: true, user }),
  clearAuth: () =>
    set({ isAuthenticated: false, isLoading: false, user: null }),
  setLoading: (isLoading) => set({ isLoading }),
}));

// -- End of User Authentication -- //

// -- Reservation Id Store -- //
interface ReservationStore {
  reservation: UserReservation | null;
  setReservation: (reservation: UserReservation) => void;
  specialRequest: string | undefined;
  setSpecialRequest: (specialRequest: string) => void;
  totalPrice: number | string | undefined;
  setTotalPrice: (totalPrice: number | string) => void;
}

export const useReservationStore = create<ReservationStore>((set) => ({
  reservation: null,
  setReservation: (reservation) => set({ reservation }),
  specialRequest: undefined,
  setSpecialRequest: (specialRequest) => set({ specialRequest }),
  totalPrice: undefined,
  setTotalPrice: (totalPrice) => set({ totalPrice }),
}));

// -- End of Reservation Id Store -- //

// -- Stripe Connection Store -- //
interface StripeConnectionStore {
  isSignUpBtnLoading: boolean;
  setIsSignUpBtnLoading: () => void;
  isPageLoading: boolean;
  setIsPageLoading: (loading: boolean) => void;
  stripeDetails: StripeDetailsProps | null;
  updateStripeDetails: (details: any) => void;
  isConfirmBtnLoading: boolean;
  setIsConfirmBtnLoading: () => void;
  isModalVisible: boolean;
  updateModalVisibility: (visible: boolean) => void;
}

export const useStripeConnectionStore = create<StripeConnectionStore>(
  (set) => ({
    isSignUpBtnLoading: false,
    setIsSignUpBtnLoading: () =>
      set((state) => ({ isSignUpBtnLoading: !state.isSignUpBtnLoading })),
    isPageLoading: true,
    setIsPageLoading: (loading: boolean) =>
      set(() => ({ isPageLoading: loading })),
    stripeDetails: null,
    updateStripeDetails: (details: any) => set({ stripeDetails: details }),
    isConfirmBtnLoading: false,
    setIsConfirmBtnLoading: () =>
      set((state) => ({ isConfirmBtnLoading: !state.isConfirmBtnLoading })),
    isModalVisible: false,
    updateModalVisibility: (visible: boolean) =>
      set(() => ({ isModalVisible: visible })),
  }),
);
// -- End of Stripe Connection Store -- //

interface ImportResult {
  id: number;
  fileName: string;
  startedAt: string; // ISO date string
  completedAt: string; // ISO date string
  totalRecords: number;
  savedRecords: number;
  failedRecords: number;
  fileUrl: string;
  errorFileUrl: string;
  ownerId: number;
  status: string;
}

interface MetaData {
  currentPage: number;
  nextPage?: number | null;
  prevPage?: number | null;
  totalPages: number;
  totalEntries: number;
}

interface ImportResponse {
  data: ImportResult[];
  meta: MetaData;
}

// -- Booking Import Store -- //
interface BookingImportStore {
  isAddModalVisible: boolean;
  updateAddModalVisibility: () => void;
  tableLoading: boolean;
  updateTableLoading: () => void;
  reservationsList: ImportResponse;
  updateReservationList: (data: any) => void;
  submitBtnLoading: boolean;
  setSubmitBtnLoading: () => void;
}

export const initialValueForReservationList = {
  data: [],
  meta: {
    currentPage: 0,
    totalPages: 0,
    totalEntries: 0,
  },
};

export const useBookingImportStore = create<BookingImportStore>((set) => ({
  isAddModalVisible: false,
  updateAddModalVisibility: () =>
    set((state) => ({ isAddModalVisible: !state.isAddModalVisible })),
  tableLoading: false,
  updateTableLoading: () =>
    set((state) => ({ tableLoading: !state.tableLoading })),
  reservationsList: initialValueForReservationList,
  updateReservationList: (details) =>
    set(() => ({
      reservationsList: {
        ...details,
        data: flattenAttributes(details?.data || []),
      },
    })),
  submitBtnLoading: false,
  setSubmitBtnLoading: () =>
    set((state) => ({ submitBtnLoading: !state.submitBtnLoading })),
}));
// -- End of Booking Import Store -- //

// -- Manage Reservation Store -- //
interface ManageReservationStore {
  isAddModalVisible: boolean;
  updateAddModalVisibility: () => void;
}

export const useManageReservationStore = create<ManageReservationStore>(
  (set) => ({
    isAddModalVisible: false,
    updateAddModalVisibility: () =>
      set((state) => ({ isAddModalVisible: !state.isAddModalVisible })),
  }),
);
// -- End of Manage Reservation Store -- //

// -- Cancel Booking Store -- //
interface CancelBookingStore {
  isCancelBookingModalVisible: boolean;
  updateCancelBookingModalVisibility: (visible: boolean) => void;
  cancelReservationId: number | string;
  updateCancelReservationId: (id: number | string) => void;
}

export const useCancelBookingStore = create<CancelBookingStore>((set) => ({
  isCancelBookingModalVisible: false,
  updateCancelBookingModalVisibility: (visible: boolean) =>
    set(() => ({ isCancelBookingModalVisible: visible })),
  cancelReservationId: '',
  updateCancelReservationId: (id: number | string) =>
    set(() => ({ cancelReservationId: id })),
}));
// -- End of Cancel Booking Store -- //

// Define the type for an add-on
export interface Addon {
  id: number;
  itemName: string;
  price: number;
  quantity: number;
  minHours: number;
  maxQuantity?: number;
  isToggle?: boolean;
}

interface AddonStore {
  addons: Addon[];
  bookingHours: string | undefined;
  setAddons: (addons: Addon[]) => void;
  updateAddon: (id: number, quantity: number) => void;
  toggleAddon: (id: number, isChecked: boolean) => void;
  setBookingHours: (hours: string) => void;
}

export const useAddonStore = create<AddonStore>((set) => ({
  addons: [],
  bookingHours: undefined,

  setAddons: (addons) => set({ addons }),
  setBookingHours: (hours) => set({ bookingHours: hours }),

  updateAddon: (id, quantity) =>
    set((state) => ({
      addons: state.addons.map((addon) =>
        addon.id === id
          ? {
            ...addon,
            quantity: Math.max(
              0,
              Math.min(quantity, addon.maxQuantity || Number.MAX_VALUE),
            ),
          }
          : addon,
      ),
    })),

  toggleAddon: (id, isChecked) =>
    set((state) => ({
      addons: state.addons.map((addon) =>
        addon.id === id ? { ...addon, quantity: isChecked ? 1 : 0 } : addon,
      ),
    })),
}));

interface EquipmentStore {
  selectedEquipment: string[];
  equipmentFilter: string[];
  setEquipmentFilter: (equipmentFilter: string[]) => void;
  toggleEquipment: (item?: string) => void;
  setSelectedEquipment: (selectedEquipment: string[]) => void;
  isEquipmentFilterOpen: boolean;
  setIsEquipmentFilterOpen: (isOpen: boolean) => void;
}

export const useEquipmentStore = create<EquipmentStore>((set, get) => ({
  selectedEquipment: [],
  equipmentFilter: [],
  isEquipmentFilterOpen: false,
  setSelectedEquipment: (selectedEquipment: string[]) =>
    set(() => ({ selectedEquipment })),
  setEquipmentFilter: (equipmentFilter: string[]) =>
    set(() => ({ equipmentFilter })),
  setIsEquipmentFilterOpen: (isEquipmentFilterOpen: boolean) =>
    set(() => ({ isEquipmentFilterOpen })),
  toggleEquipment: (item) => {
    if (!item) return; // If no item, just open the modal
    set((state) => {
      const trimmedSelectedEquipment = state.selectedEquipment.map(
        (value: string) => value.trim().toLowerCase(),
      );
      const isSelected = trimmedSelectedEquipment.includes(item.toLowerCase());
      return {
        selectedEquipment: isSelected
          ? state.selectedEquipment.filter(
              (eq) => eq.trim().toLowerCase() !== item.toLowerCase(),
            )
          : [...state.selectedEquipment, item],
      };
    });
  },
}));
